import { ref, watch, type Ref } from 'vue'
import { useUserSession } from '#imports'

/**
 * A drop-in replacement for `ref(defaultValue)` that mirrors its value
 * to localStorage so filter / search state survives reloads and panel
 * navigation.
 *
 * Usage:
 *   const search = usePersistedFilter('admin.tracks.search', '')
 *   const keyFilter = usePersistedFilter<string|null>('admin.tracks.key', null)
 *
 * Behaviour:
 *   - Server (SSR): always returns `defaultValue`. localStorage doesn't
 *     exist, so the SSR pass renders the unfiltered shell.
 *   - Client: reads the persisted value synchronously DURING setup so
 *     the first reactive `useFetch` call already uses the saved filter
 *     — no double-fetch, no flash of unfiltered content. The trade-off
 *     is a benign hydration warning on first paint, which Vue resolves
 *     by patching the affected nodes; in production builds this is
 *     silent.
 *   - Subsequent changes are written back to localStorage via a deep
 *     watcher, so arrays / objects also persist correctly.
 *   - JSON-encoded so the same composable works for strings, numbers,
 *     booleans, arrays and POJOs.
 *
 * Use a NAMESPACED key per page (`<panel>.<page>.<filter>`) so two
 * different listings don't accidentally collide on a generic name like
 * `search`. Bad keys silently fall back to `defaultValue`.
 */
// Prefix every persisted-filter key with the active user's id so two
// users sharing a browser don't see each other's filters, and so logging
// out + back in as a different account doesn't inherit the previous
// account's saved state. Falls back to "anon" before login (e.g. shop /
// public pages) — those slots get wiped on logout via clearPersistedFilters.
const PREFIX = 'dz:filter:'
function scopedKey(key: string): string {
    if (!import.meta.client) return PREFIX + 'ssr:' + key
    try {
        const { user } = useUserSession()
        const uid = (user.value as any)?._id || (user.value as any)?.id || 'anon'
        return `${PREFIX}${uid}:${key}`
    } catch {
        return `${PREFIX}anon:${key}`
    }
}

export function usePersistedFilter<T>(key: string, defaultValue: T): Ref<T> {
    let initial: T = defaultValue
    const fullKey = scopedKey(key)

    if (import.meta.client) {
        try {
            const stored = localStorage.getItem(fullKey)
            if (stored !== null) initial = JSON.parse(stored) as T
        } catch {
            // Corrupt JSON / quota / privacy-mode error — fall back to default.
        }
    }

    const state = ref(initial) as Ref<T>

    if (import.meta.client) {
        watch(state, (val) => {
            try {
                if (val === undefined || val === null || val === '') {
                    localStorage.removeItem(fullKey)
                } else {
                    localStorage.setItem(fullKey, JSON.stringify(val))
                }
            } catch {
                // Quota exceeded / privacy mode — silently give up. The
                // in-memory ref still works for the current session.
            }
        }, { deep: true })
    }

    return state
}

// Wipe every persisted filter (any user, any namespace). Called from the
// logout flow so the next login starts with a clean slate.
export function clearPersistedFilters() {
    if (!import.meta.client) return
    try {
        const keys: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i)
            if (k && k.startsWith(PREFIX)) keys.push(k)
        }
        keys.forEach(k => localStorage.removeItem(k))
    } catch {
        // ignore
    }
}
